class ProductStickyBar extends HTMLElement {
  connectedCallback() {
    this.items = [this.dataset.variantPickerSelector, this.dataset.leadTimesSelector, this.dataset.buyButtonsSelector]
      .map((selector) => document.querySelector(selector))
      .filter(Boolean);

    if (!this.items.length) return;

    // Placeholder elements mark each item's original position so it can be
    // moved back exactly where it came from — both when returning to
    // desktop, and (see settleObserver below) whenever the buy buttons'
    // natural spot on mobile scrolls into or out of view, matching where
    // they'd sit on desktop. Real elements (rather than the comment nodes
    // used previously) so the buy buttons' anchor can be watched by
    // IntersectionObserver.
    //
    // Placed BEFORE the item (not after) so the anchor's own position on
    // the page never moves: items are always re-inserted with
    // `anchor.after(item)`, i.e. after the anchor, so nothing is ever
    // inserted before it. Anchoring after the item instead would mean
    // settling — which re-inserts the item, adding its height back to the
    // page right before the anchor — pushes the anchor itself further
    // down, which can un-intersect it immediately and trigger an
    // unsettle, which removes that height and pulls it back into view,
    // re-triggering settle, and so on: a self-induced flicker loop, since
    // the observed target's own position would depend on the very
    // mutation it's triggering.
    this.anchors = this.items.map((item) => {
      const anchor = document.createElement('div');
      anchor.setAttribute('aria-hidden', 'true');
      anchor.style.cssText = 'height:0;margin:0;padding:0;border:0;overflow:hidden;';
      item.before(anchor);
      return anchor;
    });

    this.buyButtonsIndex = this.items.findIndex((item) => item.matches(this.dataset.buyButtonsSelector));
    this.product = this.closest('.product');

    if (this.buyButtonsIndex > -1 && 'IntersectionObserver' in window) {
      // root left as the default (the browsing context viewport), not
      // .product__sticky-container: this bar lives in .product__media-wrapper,
      // outside that container, so its stickiness — and therefore the
      // "has it settled" question — is relative to the actual page/viewport
      // scroll, not that container's own independent internal scroll.
      this.settleObserver = new IntersectionObserver(this.onSettleIntersect, {
        threshold: 0,
        // A small dead zone so ordinary scroll-position jitter right at
        // the boundary can't retrigger this on its own — the anchor
        // position fix above handles the main (self-induced) cause of
        // flicker, this just adds a margin of safety against the rest.
        rootMargin: '0px 0px -8px 0px',
      });
    }

    this.mql = window.matchMedia(this.dataset.breakpoint);
    this.mql.addEventListener('change', this.sync);

    this.sync();
  }

  disconnectedCallback() {
    this.mql?.removeEventListener('change', this.sync);
    this.settleObserver?.disconnect();
    window.clearTimeout(this.transitionTimeout);
  }

  sync = () => {
    // Cancel any in-flight settle/unsettle transition (see
    // onSettleIntersect) so its delayed callback can't fire after a
    // breakpoint change has already reset things underneath it.
    window.clearTimeout(this.transitionTimeout);
    this.transitioning = false;
    (this.product || this).classList.remove('product--sticky-transitioning');

    this.settled = false;
    this.classList.remove('is-settled');
    this.settleObserver?.disconnect();

    if (this.mql.matches) {
      this.items.forEach((item) => this.appendChild(item));
      if (this.settleObserver) this.settleObserver.observe(this.anchors[this.buyButtonsIndex]);
    } else {
      this.items.forEach((item, index) => this.anchors[index].after(item));
    }
  };

  onSettleIntersect = ([entry]) => {
    const shouldSettle = entry.isIntersecting;
    if (shouldSettle === this.settled || this.transitioning) return;

    // Bidirectional: moves back to the natural position once it scrolls
    // into view, and back into the bar again if the customer scrolls back
    // up past that point — matching native `position: sticky`, which would
    // do this for free, but isn't an option here since these items are
    // relocated into a physically separate element rather than styled in
    // place.
    //
    // Moving the buy buttons themselves adds/removes their height right at
    // the anchor's position, which is just as capable of re-triggering
    // this observer the instant it fires. Rather than trying to make the
    // move position-neutral, we pause the observer and fade the affected
    // areas out for the duration of the mutation, so any transient jump
    // happens while invisible, then resume once the new layout has
    // painted.
    this.transitioning = true;
    this.settleObserver.disconnect();

    const root = this.product || this;
    root.classList.add('product--sticky-transitioning');

    this.transitionTimeout = window.setTimeout(() => {
      this.settled = shouldSettle;

      if (shouldSettle) {
        this.items.forEach((item, index) => this.anchors[index].after(item));
      } else {
        this.items.forEach((item) => this.appendChild(item));
      }

      this.classList.toggle('is-settled', shouldSettle);

      // Two frames: one for the browser to apply the class/DOM changes
      // above, a second to let layout and paint (and any resulting scroll
      // adjustment) actually complete, before fading back in and
      // resuming observation.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          root.classList.remove('product--sticky-transitioning');
          this.transitioning = false;
          this.settleObserver.observe(this.anchors[this.buyButtonsIndex]);
        });
      });
    }, 150);
  };
}

customElements.define('product-sticky-bar', ProductStickyBar);
